import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import { getContestSports } from '@utils/sports';
import { RELEASE_SPORTS } from '@config/releaseProfile';

const PARTNER_ROLES = [
  { id: 'sponsor', label: 'Sponsor' },
  { id: 'organizer', label: 'Event organizer' },
] as const;

const SPONSOR_INTERESTS = [
  { id: 'contest', label: 'Contest sponsorship' },
  { id: 'tournament', label: 'Tournament / league' },
  { id: 'content', label: 'Training & content' },
  { id: 'title', label: 'Title / presenting partner' },
] as const;

const ORGANIZER_INTERESTS = [
  { id: 'tournament', label: 'Host a tournament' },
  { id: 'league', label: 'Run a league' },
  { id: 'school_event', label: 'School / academy event' },
  { id: 'multi_sport', label: 'Multi-sport festival' },
] as const;

const schema = z.object({
  partnerRole: z.enum(['sponsor', 'organizer']),
  organizationName: z.string().min(2, 'Organization name is required'),
  contactName: z.string().min(2, 'Contact name is required'),
  contactEmail: z.string().email('Valid email is required'),
  contactPhone: z.string().optional(),
  websiteUrl: z.string().optional(),
  country: z.string().min(2, 'Country is required'),
  sports: z.array(z.string()).min(1, 'Select at least one sport'),
  interests: z.array(z.string()).min(1, 'Select at least one interest'),
  eventScale: z.string().optional(),
  message: z.string().max(2000).optional(),
});

type PartnerFormValues = z.infer<typeof schema>;

const PartnersPage: React.FC = () => {
  const sports = useMemo(() => {
    const fromRelease = [...RELEASE_SPORTS];
    const extras = getContestSports().filter((s) => !fromRelease.includes(s));
    return [...fromRelease, ...extras];
  }, []);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PartnerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      partnerRole: 'sponsor',
      organizationName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      websiteUrl: '',
      country: 'India',
      sports: ['Badminton'],
      interests: ['contest'],
      eventScale: 'regional',
      message: '',
    },
  });

  const partnerRole = watch('partnerRole');
  const selectedSports = watch('sports');
  const selectedInterests = watch('interests');
  const interestOptions = partnerRole === 'organizer' ? ORGANIZER_INTERESTS : SPONSOR_INTERESTS;

  const toggleChip = (field: 'sports' | 'interests', value: string, current: string[]) => {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setValue(field, next, { shouldValidate: true });
  };

  const onSubmit = async (values: PartnerFormValues) => {
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const response = await fetch('/api/partner-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partnerRole: values.partnerRole,
          organizationName: values.organizationName.trim(),
          contactName: values.contactName.trim(),
          contactEmail: values.contactEmail.trim(),
          contactPhone: values.contactPhone?.trim() || '',
          websiteUrl: values.websiteUrl?.trim() || '',
          country: values.country.trim(),
          sports: values.sports,
          interests: values.interests,
          eventScale: values.eventScale || '',
          message: values.message?.trim() || '',
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit partnership request');
      }
      setSubmitSuccess(
        data.message ||
          'Thank you! Our partnerships team will review your request and follow up shortly.',
      );
      reset({
        partnerRole: values.partnerRole,
        organizationName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        websiteUrl: '',
        country: 'India',
        sports: values.sports.slice(0, 1),
        interests: interestOptions[0] ? [interestOptions[0].id] : [],
        eventScale: 'regional',
        message: '',
      });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  return (
    <PublicContentPageShell
      title="Sponsors & Organizers"
      subtitle="Partner with EncycGames across Developmental & Performance contests for Football, Cricket, Badminton, and more. Tell us how you want to collaborate — we will match you with the right format."
      maxWidth="md"
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: brandColors.white,
          border: '1px solid #e2e8f0',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 1.25, fontFamily: '"Sora", Arial, sans-serif' }}>
          Partnership registration
        </Typography>

        <Controller
          name="partnerRole"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={field.value}
              onChange={(_, next) => {
                if (!next) return;
                field.onChange(next);
                const defaults = next === 'organizer' ? ['tournament'] : ['contest'];
                setValue('interests', defaults, { shouldValidate: true });
              }}
              sx={{ mb: 2.5 }}
            >
              {PARTNER_ROLES.map((role) => (
                <ToggleButton
                  key={role.id}
                  value={role.id}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: brandColors.navy,
                      color: brandColors.white,
                      '&:hover': { bgcolor: '#0a0f28' },
                    },
                  }}
                >
                  {role.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {submitSuccess}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Organization / brand name"
              {...register('organizationName')}
              error={Boolean(errors.organizationName)}
              helperText={errors.organizationName?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact name"
              {...register('contactName')}
              error={Boolean(errors.contactName)}
              helperText={errors.contactName?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              {...register('contactEmail')}
              error={Boolean(errors.contactEmail)}
              helperText={errors.contactEmail?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone (optional)" {...register('contactPhone')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website (optional)" {...register('websiteUrl')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              {...register('country')}
              error={Boolean(errors.country)}
              helperText={errors.country?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="event-scale-label">Event / campaign scale</InputLabel>
              <Controller
                name="eventScale"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="event-scale-label"
                    label="Event / campaign scale"
                    value={field.value || 'regional'}
                    onChange={field.onChange}
                  >
                    <MenuItem value="local">Local / city</MenuItem>
                    <MenuItem value="regional">Regional / state</MenuItem>
                    <MenuItem value="national">National</MenuItem>
                    <MenuItem value="international">International</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: brandColors.slate, mb: 1 }}>
            Sports (multi-select)
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {sports.map((sport) => {
              const active = selectedSports.includes(sport);
              return (
                <Chip
                  key={sport}
                  label={sport}
                  onClick={() => toggleChip('sports', sport, selectedSports)}
                  sx={{
                    fontWeight: active ? 800 : 600,
                    bgcolor: active ? brandColors.navy : brandColors.ice,
                    color: active ? brandColors.white : brandColors.navy,
                    border: `1px solid ${active ? brandColors.navy : '#cbd5e1'}`,
                  }}
                />
              );
            })}
          </Stack>
          {errors.sports && <FormHelperText error>{errors.sports.message}</FormHelperText>}
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: brandColors.slate, mb: 1 }}>
            {partnerRole === 'organizer' ? 'Organizer interests' : 'Sponsorship interests'}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {interestOptions.map((item) => {
              const active = selectedInterests.includes(item.id);
              return (
                <Chip
                  key={item.id}
                  label={item.label}
                  onClick={() => toggleChip('interests', item.id, selectedInterests)}
                  sx={{
                    fontWeight: active ? 800 : 600,
                    bgcolor: active ? brandColors.blue : brandColors.ice,
                    color: active ? brandColors.white : brandColors.navy,
                    border: `1px solid ${active ? brandColors.blue : '#cbd5e1'}`,
                  }}
                />
              );
            })}
          </Stack>
          {errors.interests && <FormHelperText error>{errors.interests.message}</FormHelperText>}
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Tell us about your event or partnership goals"
          sx={{ mt: 2.5 }}
          {...register('message')}
          error={Boolean(errors.message)}
          helperText={errors.message?.message}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }} alignItems={{ sm: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={<SendIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              px: 3,
              bgcolor: brandColors.orange,
              '&:hover': { bgcolor: '#f07a3d' },
            }}
          >
            {isSubmitting ? 'Submitting…' : 'Submit partnership request'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            No account required. We use this to route you to the right EncycGames partnerships contact.
          </Typography>
        </Stack>
      </Box>
    </PublicContentPageShell>
  );
};

export default PartnersPage;
